import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'next-themes';
import { useSettingsStore, type SettingsState } from '../../lib/stores/settingsStore';

// Small utility to convert hex color to HSL string (formatted for CSS var usage)
function hexToHsl(hex: string) {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.substr(1, 2), 16);
    g = parseInt(hex.substr(3, 2), 16);
    b = parseInt(hex.substr(5, 2), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  return `${h} ${s}% ${l}%`;
}

const radiusMap: Record<string, string> = {
  none: '0rem',
  small: '0.25rem',
  medium: '0.5rem',
  large: '0.75rem',
  full: '9999px',
};

const fontSizeMap: Record<string, string> = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
};

const fontFamilyMap: Record<string, string> = {
  inter: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", Times, serif',
  mono: '"SF Mono", Monaco, Inconsolata, "Roboto Mono", "Source Code Pro", monospace',
};

const iconSizeMap: Record<string, string> = {
  xs: '12px',
  sm: '16px',
  md: '20px',
  lg: '24px',
  xl: '32px',
};

const isSettingsTheme = (value: unknown): value is SettingsState['theme'] =>
  value === 'light' || value === 'dark' || value === 'vintage' || value === 'system';

type PersistHandle = {
  hasHydrated?: () => boolean;
  onFinishHydration?: (callback: () => void) => () => void;
};

type SettingsStyleSnapshot = Pick<
  SettingsState,
  | 'accentColor'
  | 'primaryColor'
  | 'borderRadius'
  | 'fontSize'
  | 'fontFamily'
  | 'reducedMotion'
  | 'highContrast'
  | 'compactMode'
  | 'showImages'
  | 'showThumbnails'
  | 'showBackgroundElements'
  | 'showAnimations'
  | 'iconStyle'
  | 'showCategoryIcons'
  | 'showStatusIcons'
  | 'showNavigationIcons'
  | 'iconSize'
>;

const SettingsStyleProvider = () => {
  const { theme: nextTheme, resolvedTheme, setTheme } = useTheme();
  const updateSetting = useSettingsStore((state) => state.updateSetting);
  const prevThemeRef = useRef<string | null>(null);
  const prevStylesRef = useRef<string | null>(null);
  const persist = (useSettingsStore as unknown as { persist?: PersistHandle }).persist;
  const [hydrated, setHydrated] = useState(() => {
    return typeof persist?.hasHydrated === 'function' ? persist.hasHydrated() : true;
  });

  // Get all settings from store
  const theme = useSettingsStore((state) => state.theme);
  const accentColor = useSettingsStore((state) => state.accentColor);
  const primaryColor = useSettingsStore((state) => state.primaryColor);
  const borderRadius = useSettingsStore((state) => state.borderRadius);
  const fontSize = useSettingsStore((state) => state.fontSize);
  const fontFamily = useSettingsStore((state) => state.fontFamily);
  const reducedMotion = useSettingsStore((state) => state.reducedMotion);
  const highContrast = useSettingsStore((state) => state.highContrast);
  const compactMode = useSettingsStore((state) => state.compactMode);
  
  // Display settings
  const showImages = useSettingsStore((state) => state.showImages);
  const showThumbnails = useSettingsStore((state) => state.showThumbnails);
  const showBackgroundElements = useSettingsStore((state) => state.showBackgroundElements);
  const showAnimations = useSettingsStore((state) => state.showAnimations);
  
  // Icon settings
  const iconStyle = useSettingsStore((state) => state.iconStyle);
  const showCategoryIcons = useSettingsStore((state) => state.showCategoryIcons);
  const showStatusIcons = useSettingsStore((state) => state.showStatusIcons);
  const showNavigationIcons = useSettingsStore((state) => state.showNavigationIcons);
  const iconSize = useSettingsStore((state) => state.iconSize);
  
  useEffect(() => {
    if (!persist?.onFinishHydration) return;
    if (typeof persist.hasHydrated === 'function' && persist.hasHydrated()) {
      setHydrated(true);
      return;
    }
    const unsub = persist.onFinishHydration(() => setHydrated(true));
    return unsub;
  }, [persist]);

  // Sync theme with next-themes - only when store is hydrated
  useEffect(() => {
    if (!hydrated || !theme) return;
    const nextThemeCandidate = nextTheme || resolvedTheme || null;
    const nextThemeValue = isSettingsTheme(nextThemeCandidate) ? nextThemeCandidate : null;

    // First sync after hydration: prefer the currently active next-theme
    if (prevThemeRef.current === null && nextThemeValue && theme !== nextThemeValue) {
      if (typeof updateSetting === 'function') {
        updateSetting('theme', nextThemeValue);
        prevThemeRef.current = nextThemeValue;
      }
      return;
    }

    if (theme !== prevThemeRef.current) {
      setTheme(theme);
      prevThemeRef.current = theme;
    }
  }, [theme, nextTheme, resolvedTheme, setTheme, updateSetting, hydrated]);

  // Sync CSS variables - only when values actually change
  useEffect(() => {
    const currentStyles: SettingsStyleSnapshot = {
      accentColor,
      primaryColor,
      borderRadius,
      fontSize,
      fontFamily,
      reducedMotion,
      highContrast,
      compactMode,
      showImages,
      showThumbnails,
      showBackgroundElements,
      showAnimations,
      iconStyle,
      showCategoryIcons,
      showStatusIcons,
      showNavigationIcons,
      iconSize,
    };
    const currentStylesKey = JSON.stringify(currentStyles);

    // Only update if styles have actually changed
    if (currentStylesKey !== prevStylesRef.current) {
      const root = document.documentElement;
      if (!root) return;

      // Colors
      if (accentColor) {
        root.style.setProperty('--accent', hexToHsl(accentColor));
      }
      if (primaryColor) {
        root.style.setProperty('--primary', hexToHsl(primaryColor));
      }

      // Typography
      root.style.setProperty('--radius', radiusMap[borderRadius] || '0.5rem');
      root.style.setProperty('--fs-base', fontSizeMap[fontSize] || '1rem');
      root.style.setProperty('--font-family', fontFamilyMap[fontFamily] || fontFamilyMap.inter);

      // Icon settings
      root.style.setProperty('--icon-size', iconSizeMap[iconSize] || '20px');

      // Data attributes for feature toggles
      const updateFlag = (attr: string, enabled: boolean) => {
        if (enabled) {
          root.setAttribute(attr, '');
        } else {
          root.removeAttribute(attr);
        }
      };

      // Appearance flags
      updateFlag('data-high-contrast', highContrast);
      updateFlag('data-compact', compactMode);
      updateFlag('data-reduced-motion', reducedMotion);
      
      // Display flags
      updateFlag('data-show-images', showImages);
      updateFlag('data-show-thumbnails', showThumbnails);
      updateFlag('data-show-background-elements', showBackgroundElements);
      updateFlag('data-show-animations', showAnimations);
      
      // Icon flags
      updateFlag('data-show-category-icons', showCategoryIcons);
      updateFlag('data-show-status-icons', showStatusIcons);
      updateFlag('data-show-navigation-icons', showNavigationIcons);
      root.setAttribute('data-icon-style', iconStyle);
      
      prevStylesRef.current = currentStylesKey;
    }
  }, [
    accentColor, primaryColor, borderRadius, fontSize, fontFamily, reducedMotion, 
    highContrast, compactMode, showImages, showThumbnails, showBackgroundElements, 
    showAnimations, iconStyle, showCategoryIcons, showStatusIcons, showNavigationIcons, 
    iconSize
  ]);

  return null;
};

export default SettingsStyleProvider; 
